import { MainTemplate } from 'Components/Template/MainTemplate'
import { Container, Stack, ThemeProvider, Dialog, DialogContent } from '@mui/material'
import { observer } from 'mobx-react-lite'
import styled from '@emotion/styled'
import { lazy, useEffect, useMemo, useState } from 'react'
import pMinDelay from 'p-min-delay'
import { useNavigate, useParams } from 'react-router-dom'
import { autorun, reaction, runInAction } from 'mobx'
import StoreFormat from 'GlobalStores/Format/StoreFormat'
import { CreditCardHelper } from 'Helpers/CreditCardHelper'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { storeElement } from 'GlobalStores/StoreElement'
import { get } from 'lodash'
import StoreAuth from 'GlobalStores/User/StoreAuth'
import StoreUser from 'GlobalStores/User/StoreUser'
import DefaultCheckoutTheme from '../DefaultCheckoutTheme'
import storeReferralCheckout from './Stores/StoreReferralCheckout'
import DuplicatedOrder from 'Components/Elements/DuplicatedOrder/DuplicatedOrder'
import { ReferralUaeAPIs } from './Services/ReferralUaeAPIs'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import CheckoutLoaderStore from 'GlobalStores/Checkout/CheckoutLoader'
import PaymentProcessBar from 'Components/Elements/LoadingBar/PaymentProcessBar'
import StorePaymentStep from 'GlobalStores/Cores/Elements/StorePaymentStep'
import StoreReferralCart from './Stores/StoreReferralCart'
import StoreOrderCalc from 'GlobalStores/Cores/OrderCalc/StoreOrderCalc'
import StoreReferralFormat from './Stores/StoreReferralFormat'
import trackingEvents from 'Services/Tracker/TrackingEvents'
import storeReferral from './Stores/StoreReferral'
import StoreAddEditProduct, { COMMIT_ERROR_KEYS, commitErrorText } from './Stores/StoreAddEditProduct'
import { trackingItems } from './Helpers/addonHelpers'
import { checkoutErrorMessages, placeOrderFailedText } from './Helpers/checkoutErrors'
import StoreLoader from 'GlobalStores/Cores/Elements/StoreLoader'
import { completeSuccessfulOrder } from './Helpers/guestCheckoutCompletion'

// const ShippingAddressBoxLazyLoad = lazy(() => pMinDelay(import('../Elements/Checkout/ShippingAddress'), 0))
const PaymentBoxLazyLoad = lazy(() => pMinDelay(import('./Components/OrderSummary/ReferralPayment'), 0))
const PreSummaryBoxLazyLoad = lazy(() => pMinDelay(import('../Uae/Components/OrderSummary/V2/OrderSummaryItemCard'), 0))
// const PaymentReviewLazyLoad = lazy(() => pMinDelay(import('../Elements/Checkout/PaymentReview'), 0))
// const CreateAccountWithShippingLazyLoad = lazy(() => pMinDelay(import('../Elements/Checkout/CreateAccountWithShipping'), 0))

const CheckoutPage = observer(() => {
    const params = useParams()
    const navigate = useNavigate()
    const translate = useTranslate()
    const [openLoginModal, setOpenLoginModal] = useState(false)
    const [openDuplicatedModal, setOpenDuplicatedModal] = useState(false)
    const [duplicatedData, setDuplicatedData] = useState({
        paymentId: '',
        dateAndTime: '',
        totalPrice: '',
        currency: '',
        is_duplicate: false
    })

    const qs = useMemo(() => new URLSearchParams(window.location.search), [])

    const [sectionReady, setSectionReady] = useState(false)

    const closeDuplicateModal = () => {
        setOpenDuplicatedModal(false)
        runInAction(() => {
            storeElement.showCardFrontDrop = false
        })
    }

    // GA4 payload for both events. It reads the cart, so add-on rows are included — the old lookup went
    // through detail_page.items, which never carries them, and through options[paymentOption], which an
    // add-on does not have outside onetime.
    const trackingProducts = () =>
        trackingItems(
            StoreReferralCart.items.filter(item => item.qty > 0),
            StoreReferralFormat.paymentOptionSelected,
            StoreReferralCart.hasDiscount()
        )

    const checkDuplicateOrder = async () => {
        try {
            const items = trackingProducts()
            const totalPrice = items.map(item => item.price * item.qty).reduce((a, b) => a + b, 0)

            trackingEvents.BeginCheckout(storeCountry.CountryLowerCase(), trackingEvents.EVENT_TYPE.BEGIN_CHECKOUT.toLowerCase(), items, totalPrice, storeReferral.GetReferral(), '')

            if (/subscribe/.test(JSON.stringify(StoreReferralCart.getCartItems()))) {
                runInAction(() => {
                    CheckoutLoaderStore.orderApiProgress.createAutoship = 0
                })
            } else {
                runInAction(() => {
                    CheckoutLoaderStore.orderApiProgress.createAutoship = -1
                })
            }
            runInAction(() => {
                storeReferralCheckout.paymentErrors = []
                storeElement.showCardFrontDrop = true
                storeReferralCheckout.paymentLoading = true
            })
            const checkDuplicateOrder = await ReferralUaeAPIs.checkDuplicateOrder({
                countryCode3Alpha: storeCountry.Country3(),
                uuid: storeReferralCheckout.GetUuid() || qs.get('uuid')
            })

            if (checkDuplicateOrder.data.is_duplicate === true) {
                setDuplicatedData({
                    paymentId: checkDuplicateOrder.data.payments_id,
                    dateAndTime: checkDuplicateOrder.data.date_time,
                    totalPrice: checkDuplicateOrder.data.total_price,
                    is_duplicate: true,
                    currency: checkDuplicateOrder.data.currency
                })
                setOpenDuplicatedModal(true)
            } else {
                await onPlaceOrder()
            }
        } catch (e) {
            console.log('error', e)
            runInAction(() => {
                // Same rule as the order itself: if the duplicate check cannot run, the customer is told
                // rather than left with a button that appears to do nothing.
                storeReferralCheckout.paymentErrors = withUuidReference([placeOrderFailedText()])
                storeReferralCheckout.paymentLoading = false
            })
            closeDuplicateModal()
        }
    }

    const onPlaceOrder = async () => {
        const items = trackingProducts()

        try {
            runInAction(() => {
                storeReferralCheckout.paymentErrors = []
                storeElement.showCardFrontDrop = true
                storeReferralCheckout.paymentLoading = true
            })

            // * Check Out of Stock
            const isOutOfStock = storeReferralCheckout.CheckOutofStockCartItems()
            if (isOutOfStock?.length > 0) {
                const formatErrMessage = isOutOfStock.map(item => translate('error_ufg_validation_oos_product').replace('{{productName}}', item))
                console.log('formatErrMessage', formatErrMessage)
                runInAction(() => {
                    storeElement.showCardFrontDrop = false
                    storeReferralCheckout.paymentErrors = [...formatErrMessage]
                    storeReferralCheckout.paymentLoading = false
                })

                return
            }

            // Creditcard section
            const creditCard = {
                countryCode: 'AE',
                creditCard: {
                    creditCardNumber: GetCardValue('card_number'),
                    payer: GetCardValue('name_on_card'),
                    creditCardExpires: GetCardValue('exp_date'),
                    creditCardSecurityCode: GetCardValue('cvv')
                }
            }

            const cardValidate = CreditCardHelper.cardValidate(creditCard)

            if (cardValidate.success) {
                if (get(StoreFormat.GetDetailPage(), 'type') !== 'enroll' && !StoreAuth.GetToken()) {
                    await StoreAuth.Login({ unicityID: StoreUser.baId, password: StoreUser.password })
                }
            } else {
                const query = new URLSearchParams(document.location.search)
                let cardErrors = []
                cardValidate.error_messages.map(item => {
                    // cardErrors.push({
                    //     english: item,
                    //     native: item
                    // })
                    cardErrors.push(item)
                    return item
                })
                cardErrors.push(`UUID : ${query.get('uuid')}`)
                runInAction(() => {
                    storeElement.showCardFrontDrop = false
                    storeReferralCheckout.paymentErrors = cardErrors
                    storeReferralCheckout.paymentLoading = false
                })
                return
            }

            const creditCardData = storeReferralCheckout.CreditCardEncrypted(creditCard.creditCard, StoreFormat.GetDetailPage().email)

            // The session must hold the right items before any money moves. A failure here used to be
            // swallowed and the payment went out against whatever the session still had.
            const sessionUpdate =
                StoreAuth.GetToken() && !storeReferralCheckout.paymentFromEnroll
                    ? await storeReferralCheckout.UpdateItemdata()
                    : await storeReferralCheckout.UpdateItemdataCreateAccount()

            if (!sessionUpdate.success) {
                if (sessionUpdate.isInvalidItemCode) {
                    StoreAddEditProduct.MarkUnavailable(sessionUpdate.itemCodes)
                    await StoreAddEditProduct.RefreshPublish()
                }

                // What the endpoint rejected beats our own guess at it: Message_v2 names the field
                // ("error_city_empty"), our line only says the cart could not be updated.
                const apiMessages = checkoutErrorMessages(sessionUpdate.messages, get(StoreFormat.GetDetailPage(), 'country'))

                runInAction(() => {
                    storeElement.showCardFrontDrop = false
                    storeReferralCheckout.paymentErrors =
                        apiMessages.length > 0
                            ? apiMessages
                            : [commitErrorText(sessionUpdate.isInvalidItemCode ? COMMIT_ERROR_KEYS.INVALID_ITEM_CODE : COMMIT_ERROR_KEYS.UPDATE_FAILED)]
                    storeReferralCheckout.paymentLoading = false
                })

                return
            }

            const response = await storeReferralCheckout.SubmitPayment({
                uuid: storeReferralCheckout.GetUuid(),
                detailPage: StoreFormat.GetDetailPage(),
                CreditCardEncrypted: creditCardData,
                products: items
            })

            if (response.success) {
                const completedCheckoutUuid = storeReferralCheckout.GetUuid() || qs.get('uuid')
                await completeSuccessfulOrder({
                    response,
                    uuid: completedCheckoutUuid,
                    checkoutStore: storeReferralCheckout,
                    finishOrderState: () => {
                        runInAction(() => {
                            storeElement.showCardFrontDrop = true
                            StorePaymentStep.PaymentDone = true
                            StoreReferralCart.ResetAfterSuccessfulOrder()
                            StoreAddEditProduct.ResetDraftState()
                            storeReferralCheckout.ResetAfterSuccessfulOrder()
                            StoreOrderCalc.resetOrderCalc()
                        })
                    },
                    navigate
                })
            } else {
                // The order did not go through. Something has to be on the page: a 504 from
                // createAccount used to leave the customer looking at the progress bar with no reason
                // given. Prefer what the API reported, and always add the uuid — support traces by it,
                // the same way the card-validation errors do.
                const apiMessages = checkoutErrorMessages(response.messages, get(StoreFormat.GetDetailPage(), 'country'))

                runInAction(() => {
                    storeElement.showCardFrontDrop = false
                    storeReferralCheckout.paymentErrors = withUuidReference(
                        apiMessages.length > 0 ? apiMessages : [placeOrderFailedText()]
                    )
                    storeReferralCheckout.paymentLoading = false
                })
            }
        } catch (e) {
            console.log('error', e)
            runInAction(() => {
                storeElement.showCardFrontDrop = false
                storeReferralCheckout.paymentErrors = withUuidReference([placeOrderFailedText()])
                storeReferralCheckout.paymentLoading = false
            })
        }
    }

    // The Errors block draws a dashed line between entries, so the reference reads as its own line under
    // the message — the shape the card-validation errors already use.
    const withUuidReference = messages => [...messages, `UUID : ${storeReferralCheckout.GetUuid() || qs.get('uuid') || '-'}`]

    const GetCardValue = key => {
        const card = storeReferralCheckout.creditCard.find(item => item.label === key)
        if (card) {
            return card.value
        }
        return ''
    }

    useEffect(() => {
        if (qs.get('test-duplicated')) {
            setDuplicatedData({
                paymentId: '123123215415',
                dateAndTime: '2024-03-04 23:55:43',
                totalPrice: '580',
                is_duplicate: true,
                currency: 'AED'
            })
            setTimeout(() => {
                setOpenDuplicatedModal(true)
            }, 1000)
        }

        if (qs.get('uuid')) {
            storeReferralCheckout.SetUuid(qs.get('uuid'))
        }
    }, [qs])

    useEffect(() => {
        storeReferralCheckout.paymentErrors = []
        runInAction(() => {
            storeElement.showCardFrontDrop = false
            storeReferralCheckout.paymentErrors = []
            storeReferralCheckout.paymentLoading = false
            StoreOrderCalc.resetOrderCalc()
            // StoreReferralCart.ResetAllItemQtytoOne()
        })

        reaction(
            () => StorePaymentStep.PaymentDone,
            (n, p, d) => {
                if (n) {
                    storeReferralCheckout.paymentLoading = false
                    d.dispose()
                }
            }
        )

        // A guest who reloads this page has lost their password — it only ever lived in memory, and
        // Place order needs it to create the account. Nothing on the page can recover it, so send them
        // back to the form for that one field; the session refills everything else. The uuid rides
        // along so the form still knows the order, and so a second reload lands in the same place.
        // This waits inside an autorun because the session and the page arrive in either order.
        const disposeGuestGuard = autorun(disposer => {
            if (StoreLoader.IsFormatApiLoading() || !storeReferralCheckout.MissingGuestPassword()) {
                return
            }

            disposer.dispose()
            const uuid = storeReferralCheckout.GetUuid() || qs.get('uuid')
            storeReferralCheckout.SetReturnedForPassword(true)
            navigate(`/checkout/create-account${uuid ? `?uuid=${uuid}` : ''}`, { replace: true })
        })

        return () => disposeGuestGuard()
    }, [])

    const Sections = step => {
        return <PaymentBoxLazyLoad onPlaceOrder={checkDuplicateOrder} sectionReady={setSectionReady} />
    }

    const PreSummarySection = () => {
        return <PreSummaryBoxLazyLoad />
    }

    const CheckoutThemesSelector = () => {
        return (
            <StackStyled stepPaymentOrReview={params.step === 'review'}>
                <DefaultCheckoutTheme
                    onPlaceOrder={checkDuplicateOrder}
                    step={params.step}
                    Sections={Sections}
                    setOpenLoginModal={setOpenLoginModal}
                    openLoginModal={openLoginModal}
                    PreSummarySection={PreSummarySection}
                />
            </StackStyled>
        )
    }

    const confirmPlaceOrder = async () => {
        console.log('confirm place order')
        setOpenDuplicatedModal(false)
        runInAction(() => {
            storeReferralCheckout.paymentErrors = []
            storeElement.showCardFrontDrop = true
            storeReferralCheckout.paymentLoading = true
        })
        await onPlaceOrder()
    }

    return (
        <ThemeProvider theme={storeElement.theme}>
            <Stack backgroundColor={MainTemplate.palette.background.default} minHeight="100vh">
                <ContainerStyled maxWidth="lg">
                    <Stack>{CheckoutThemesSelector()}</Stack>
                    <DialogDuplicatedStyled open={openDuplicatedModal}>
                        <DialogContent
                            sx={{
                                padding: 0
                            }}>
                            <DuplicatedOrder confirmPlaceOrder={confirmPlaceOrder} setOpenModal={closeDuplicateModal} {...duplicatedData} />
                        </DialogContent>
                    </DialogDuplicatedStyled>
                    <PaymentProcessBar
                        isModalOpen={storeReferralCheckout.paymentLoading}
                        onClose={() => {
                            storeReferralCheckout.paymentLoading = false
                            CheckoutLoaderStore.orderApiReady = false

                            CheckoutLoaderStore.orderApiProgress = {
                                hydraCreateAccount: 0,
                                createOrder: 0,
                                login: 0,
                                createAutoship: 0
                            }
                        }}
                    />
                </ContainerStyled>
            </Stack>
        </ThemeProvider>
    )
})
const ContainerStyled = styled(Container)`
    @media screen and (min-width: 730px) and (max-width: 1200px) {
        padding: 0 70px;
    }
`

const StackStyled = styled.div`
    padding-top: 40px;
    padding-bottom: 40px;
    min-height: 50vh;
    display: flex;
    flex-direction: ${props => (props.stepPaymentOrReview ? 'column' : 'column-reverse')};
    justify-content: ${props => (props.stepPaymentOrReview ? 'flex-start' : 'flex-end')};
    gap: 20px;

    @media screen and (min-width: 399px) {
        padding-top: 40px;
    }

    @media screen and (min-width: 600px) {
        padding-top: 60px;
    }

    @media screen and (min-width: 1200px) {
        display: grid;
        grid-template-columns: 770px 380px;
        gap: 22px;
        padding-top: 40px;
    }
`

const DialogDuplicatedStyled = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        box-shadow: none;
        @media screen and (min-width: 768px) {
            width: 100%;
            max-width: 650px;
        }
    }
`

export default CheckoutPage
